import type {
  InfiniteData} from "react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AxiosResponse } from "axios";
import { AxiosError } from "axios";
import { message } from "antd";
import * as Sentry from "@sentry/react";
import api from "../utils/axiosConfig";
import { logout } from "../store/userSlice";
import type {
  ProfileType,
  UserDataType,
  ProfileResponse,
  FavouriteResponse,
  InterestRequestResponse,
  ObjectId,
  GetSettingsResponse,
  CountryResponse,
  ApiStatusError,
  GetBillResponse,
  GetBillErrorResponse,
  VerifyTransactionResponse,
  HoroscopePurchaseStatusResponse,
  HoroscopePurchaseStatusAPIError,
  DeactivateProfileResponse,
  DeactivateProfileError,
  GetPaymentHistoryResponse,
  CheckHoroscopeMatchPaymentStatusResponse,
  CheckHoroscopeMatchPaymentStatusError,
  HoroscopeMatchPurchaseError,
  HoroscopePurchaseError,
  GetHoroscopeServicesResponse,
  JobTitleResponse,
  JobResponse,
  SpecializationResponse,
  GetCountryResponse,
  GetNotificationsResponse,
  ErrorResponse,
  DeleteNotificationResponse,
  DeleteNotificationAPIError,
  GetIgnoredResponse,
  APIErrorResponse,
  GetProfilesResponse,
  ApiError,
  SubscriptionResponse,
  SubscriptionAPIError,
  SendOtpResponse,
  VerifyOtpResponse,
  LoginResponse,
  GetOnBoardingResponse,
  SubmitOnBoardingResponse,
  CreateProfileResponse,
  AddRegisterResponse,
  PhotoUploadResponse,
  UploadHoroscopeApiResponse,
  DownloadResponse,
  SendRequestResponse,
  APIToggleIgnoreErrorResponse,
  ToggleIgnoreResponse,
  DeclineInterestResponse,
  InitiateRzPurchasePlanResponse,
  HoroscopeRzPurchaseResponse,
  HoroscopeMatchRzPurchaseResponse,
  HoroscopeMatchPurchaseResponse,
  HoroscopePurchaseResponse,
  InitiatePurchasePlanResponse,
  ReadUpdateNotificationResponse,
  ReadUpdateNotificationAPIError,
} from "./types";
import { capitalizeName } from "../utils/capitalize";
import { QUERY_LIMIT } from "../constants";

{
  /* get state */
}

export const fetchRegionOptions = async (
  countryId: number,
  searchTerm: string,
  limit = QUERY_LIMIT
): Promise<GetSettingsResponse> => {
  if (!countryId) {
    return Promise.reject(
      new Error("Country ID is required to fetch regions.")
    );
  }
  try {
    const response = await api.get<GetSettingsResponse>("/v1/settings/region", {
      params: { country_id: countryId, q: searchTerm, limit },
    });

    const transformedData = {
      ...response.data,
      data: response.data.data.map((staate) => ({
        ...staate,
        name: capitalizeName(staate.name),
      })),
    };

    return transformedData;
  } catch (error: any) {
    console.error(`Error fetching regionOptions:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useRegionOptions = (
  countryId: number,
  searchTerm: string = "",
  limit: number = 30
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["regionOptions", countryId, searchTerm],
    queryFn: () =>
      countryId
        ? fetchRegionOptions(countryId, searchTerm, limit)
        : Promise.reject("No country selected"),
    onError: (error) => {
      console.error("Error in useRegionOptions:", error);
      Sentry.captureException(error);
    },
    enabled: !!countryId,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

{
  /* get place */
}

export const fetchPlaceOptions = async (
  districtId: number,
  searchTerm = "",
  limit = QUERY_LIMIT
): Promise<GetSettingsResponse> => {
  if (!districtId) throw new Error("Invalid district ID");
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/panchayath",
      {
        params: { district_id: districtId, q: searchTerm, limit },
      }
    );

    const transformedData = {
      ...response.data,
      data: response.data.data.map((place) => ({
        ...place,
        name: capitalizeName(place.name),
      })),
    };

    return transformedData;
  } catch (error: any) {
    console.error(`Error fetching placeOptions:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const usePlaceOptions = (
  districtId: number,
  searchTerm: string,
  limit = QUERY_LIMIT
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["placeOptions", districtId, searchTerm],
    queryFn: () => fetchPlaceOptions(districtId, searchTerm, limit),
    onError: (error) => {
      console.error("Error in usePlaceOptions:", error);
      Sentry.captureException(error);
    },
    enabled: !!districtId,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

{
  /* get country */
}

export const fetchCountryOptions = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<CountryResponse> => {
  try {
    const response = await api.get<CountryResponse>("/v1/settings/country", {
      params: { page, limit, q: searchTerm },
    });
    const transformedData = {
      ...response.data,
      data: response.data.data.map((country) => ({
        ...country,
        name: capitalizeName(country.name),
      })),
    };

    return transformedData;
  } catch (error: any) {
    console.error(`Error fetching countryOptions:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useCountryOptions = (
  page = 1,
  limit = 400,
  searchTerm: string
) => {
  return useQuery<CountryResponse, Error>({
    queryKey: ["countryOptions", page, searchTerm],
    queryFn: () => fetchCountryOptions(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useCountryOptions:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });
};

{
  /* get city */
}

export const fetchCityOptions = async (
  stateId: number,
  searchTerm = "",
  limit = QUERY_LIMIT
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/district",
      {
        params: { region_id: stateId, q: searchTerm, limit },
      }
    );
    const transformedData = {
      ...response.data,
      data: response.data.data.map((city) => ({
        ...city,
        name: capitalizeName(city.name),
      })),
    };
    return transformedData;
  } catch (error: any) {
    console.error(`Error fetching cityOptions:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useCityOptions = (stateId: number, searchTerm: string) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["cityOptions", stateId, searchTerm],
    queryFn: () => fetchCityOptions(stateId, searchTerm),
    onError: (error) => {
      console.error("Error in useCityOptions:", error);
      Sentry.captureException(error);
    },
    enabled: !!stateId,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get  my detail
 */
const getMyDetail = async (): Promise<
  AxiosResponse<UserProfileApiResponse>
> => {
  try {
    const response = await api.get<UserProfileApiResponse>(
      `/v1/user/profile/mine`
    );
    return response;
  } catch (error: any) {
    console.error(`Error fetching my detail:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useGetMyDetail = () => {
  const navigate = useNavigate();
  return useQuery<UserProfileApiResponse, unknown>({
    queryKey: ["myDetail"],
    queryFn: async () => {
      const response = await getMyDetail();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: 2,
    onError: (error: any) => {
      console.error("Error in useGetMyDetail:", error);
      Sentry.captureException(error);
      if (error?.data?.message) {
        message.error(error.data.message);
      }
      navigate("/home");
    },
  });
};

/**
 * Get Bill
 */

const fetchBill = async (transactionId: string): Promise<GetBillResponse> => {
  try {
    const response = await api.get<GetBillResponse>("/v1/plans/bill", {
      params: { transationId: transactionId },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching bill details:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useGetBill = (transactionId: string) => {
  return useQuery<GetBillResponse, GetBillErrorResponse>({
    queryKey: ["bill", transactionId],
    queryFn: () => fetchBill(transactionId),
    onError: (error) => {
      console.error("Error in useGetBill:", error);
      Sentry.captureException(error);
    },
    enabled: !!transactionId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

/**
 * Fetch plan history payment status
 */

const fetchTransactionStatus = async (
  merchantOrderId: string
): Promise<VerifyTransactionResponse> => {
  try {
    const response = await api.get<VerifyTransactionResponse>(
      "/v1/plans/plan-history/status",
      {
        params: { merchantOrderId },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching transaction status:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useVerifyTransaction = (merchantOrderId: string) => {
  const queryClient = useQueryClient();
  return useQuery<VerifyTransactionResponse, ErrorResponse>({
    queryKey: ["transaction-status", merchantOrderId],
    queryFn: () => fetchTransactionStatus(merchantOrderId),
    enabled: !!merchantOrderId,
    staleTime: 60 * 1000,
    retry: false,
    onSuccess: (data) => {
      if (data.message) {
        message.info(data.message);
      }
      queryClient.invalidateQueries(["myDetail"]);
      queryClient.invalidateQueries(["paymentHistory"]);
    },
    onError: (error) => {
      console.error("Error in useVerifyTransaction:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Fetch horoscope payment status
 */

const fetchHoroscopePurchaseStatus = async (
  merchantTransactionId: string
): Promise<HoroscopePurchaseStatusResponse> => {
  try {
    const response = await api.get<HoroscopePurchaseStatusResponse>(
      `/v1/payment/horoscope/status/${merchantTransactionId}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope purchase status:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useHoroscopePurchaseStatus = (
  merchantTransactionId: string,
  enabled = true
) => {
  return useQuery<
    HoroscopePurchaseStatusResponse,
    HoroscopePurchaseStatusAPIError
  >({
    queryKey: ["horoscope-status", merchantTransactionId],
    queryFn: () => fetchHoroscopePurchaseStatus(merchantTransactionId),
    onError: (error) => {
      console.error("Error in useHoroscopePurchaseStatus:", error);
      Sentry.captureException(error);
    },
    enabled: !!merchantTransactionId && enabled,
    staleTime: 2 * 60 * 1000, // short TTL since status may change rapidly
    cacheTime: 5 * 60 * 1000,
    retry: 1, // don't hammer server
  });
};

/**
 * Deactivate Profile
 */

const deactivateProfile = async (
  otp: string
): Promise<DeactivateProfileResponse> => {
  try {
    const response = await api.post<DeactivateProfileResponse>(
      "/v1/user/profile/deactivate",
      { otp }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error deactivate:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useDeactivateProfile = () => {
  return useMutation<DeactivateProfileResponse, DeactivateProfileError, string>(
    (otp: string) => deactivateProfile(otp)
  );
};

/**
 * Get Payment History
 */
const fetchPaymentHistory = async ({
  pageParam = 1,
  limit,
}: {
  pageParam?: number;
  limit?: number;
}): Promise<AxiosResponse<GetPaymentHistoryResponse>> => {
  return await api.get<GetPaymentHistoryResponse>("/v1/plans/payment-history", {
    params: { page: pageParam, limit },
  });
};

export const usePaymentHistoryInfinite = (limit = QUERY_LIMIT) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: ["paymentHistory", limit],
    queryFn: ({ pageParam = 1 }) => fetchPaymentHistory({ pageParam, limit }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.data.pagination.page;
      const totalPages = lastPage.data.pagination.total_pages;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    onSuccess: () => {
      queryClient.invalidateQueries(["myDetail"]);
    },
    select: (data) => ({
      pages: data.pages.flatMap((page) => page.data.data ?? []),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error fetching payment history:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Check Status for Horoscope Match Request
 */

export const fetchHoroscopeMatchPaymentStatus = async (
  merchantTransactionId: string
): Promise<CheckHoroscopeMatchPaymentStatusResponse> => {
  try {
    const response = await api.get<CheckHoroscopeMatchPaymentStatusResponse>(
      `/v1/user/horoscope-match/status/${merchantTransactionId}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope match payment status:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useCheckHoroscopeMatchPaymentStatus = (
  merchantTransactionId: string
) => {
  return useQuery<
    CheckHoroscopeMatchPaymentStatusResponse,
    CheckHoroscopeMatchPaymentStatusError
  >({
    queryKey: ["horoscopeMatchPaymentStatus", merchantTransactionId],
    queryFn: () => fetchHoroscopeMatchPaymentStatus(merchantTransactionId),
    onError: (error) => {
      console.error("Error in useCheckHoroscopeMatchPaymentStatus:", error);
      Sentry.captureException(error);
    },
    enabled: !!merchantTransactionId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Create Match Horoscope Request
 */

const purchaseHoroscopeMatch = async (
  targetUserId: string
): Promise<HoroscopeMatchPurchaseResponse> => {
  try {
    const response = await api.post<HoroscopeMatchPurchaseResponse>(
      "/v1/user/horoscope-match/",
      {
        targetUserId,
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope match:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const usePurchaseHoroscopeMatch = () => {
  return useMutation<
    HoroscopeMatchPurchaseResponse,
    HoroscopeMatchPurchaseError,
    string
  >((targetUserId: string) => purchaseHoroscopeMatch(targetUserId));
};

/**
 * Create Match Horoscope Request Rz
 */

const purchaseRzHoroscopeMatch = async (
  targetUserId: string
): Promise<HoroscopeMatchRzPurchaseResponse> => {
  try {
    const response = await api.post<HoroscopeMatchRzPurchaseResponse>(
      "/v1/user/horoscope-match/",
      {
        targetUserId,
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope match:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const usePurchaseRzHoroscopeMatch = () => {
  return useMutation<
    HoroscopeMatchRzPurchaseResponse,
    HoroscopeMatchPurchaseError,
    string
  >((targetUserId: string) => purchaseRzHoroscopeMatch(targetUserId));
};

/**
 * Purchase Create Horoscope
 */

const purchaseHoroscope = async (): Promise<HoroscopePurchaseResponse> => {
  try {
    const response = await api.post<HoroscopePurchaseResponse>(
      "/v1/user/horoscope/"
    );
    return response.data;
  } catch (error: any) {
    const apiError: HoroscopePurchaseError = {
      error: error.response?.data?.message || "Unknown error",
      details: error.response?.data || error.message,
    };
    throw apiError;
  }
};

export const usePurchaseHoroscope = () => {
  return useMutation<HoroscopePurchaseResponse, HoroscopePurchaseError>(
    purchaseHoroscope
  );
};

/**
 * Purchase Create Horoscope Rz
 */

const RzPurchaseHoroscope = async (): Promise<HoroscopeRzPurchaseResponse> => {
  try {
    const response = await api.post<HoroscopeRzPurchaseResponse>(
      "/v1/user/horoscope/"
    );
    return response.data;
  } catch (error: any) {
    const apiError: HoroscopePurchaseError = {
      error: error.response?.data?.message || "Unknown error",
      details: error.response?.data || error.message,
    };
    throw apiError;
  }
};

export const useRzPurchaseHoroscope = () => {
  return useMutation<HoroscopeRzPurchaseResponse, HoroscopePurchaseError>(
    RzPurchaseHoroscope
  );
};

/**
 * Get Horoscope Create Status
 */

const fetchHoroscopeCreateStatus = async ({
  pageParam = 1,
}): Promise<GetHoroscopeServicesResponse> => {
  try {
    const response = await api.get<GetHoroscopeServicesResponse>(
      "/v1/user/horoscope/",
      {
        params: { page: pageParam, limit: QUERY_LIMIT },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope crate status:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useGetCreateHoroscopeServiceStatus = () => {
  return useInfiniteQuery<GetHoroscopeServicesResponse, Error>({
    queryKey: ["horoscope-services"],
    queryFn: fetchHoroscopeCreateStatus,
    onError: (error) => {
      console.error("Error in useGetCreateHoroscopeServiceStatus:", error);
      Sentry.captureException(error);
    },
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pagination.page + 1;
      return nextPage <= lastPage.pagination.total_pages ? nextPage : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    keepPreviousData: true,
  });
};

/**
 * Get Horoscope Raasi
 */

const fetchRaasi = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/horoscope-raasi",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching raasi:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useHoroscopeRaasi = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["raasi", page, searchTerm],
    queryFn: () => fetchRaasi(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useHoroscopeRaasi:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get Horoscope Dosham
 */

const fetchDosham = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/horoscope-dosham",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching dosham:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useHoroscopeDosham = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["dosham", page, searchTerm],
    queryFn: () => fetchDosham(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useHoroscopeDosham:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get horoscope preferance
 */

const fetchHoroscopePreferences = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/horoscope-prefences",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching horoscope preference:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useHoroscopePreferences = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["horoscopePreferences", page, searchTerm],
    queryFn: () => fetchHoroscopePreferences(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useHoroscopePreferences:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get Horoscope Star
 */

const fetchHoroscopeStars = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/horoscope-star",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching star:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useHoroscopeStars = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["horoscopeStars", page, searchTerm],
    queryFn: () => fetchHoroscopeStars(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useHoroscopeStars:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get Eating Habits
 */

const fetchEatingHabits = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/eating-habits",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching eating habits:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useEatingHabits = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["eatingHabits", page, searchTerm],
    queryFn: () => fetchEatingHabits(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useEatingHabits:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get NetWorth
 */

const fetchFamilyNetworth = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/family-networth",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching networth:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useFamilyNetworth = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["familyNetworth", page, searchTerm],
    queryFn: () => fetchFamilyNetworth(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useFamilyNetworth:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get Family status
 */

const fetchFamilyStatus = async (
  page: number = 1,
  limit: number = 100,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/family-status",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching family status:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useFamilyStatus = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["familyStatus", page, searchTerm],
    queryFn: () => fetchFamilyStatus(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useFamilyStatus:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get income options
 */

const fetchIncomeOptions = async (
  page: number = 1,
  limit: number = 100,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/annual-income",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching income:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useIncomeOptions = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["incomeOptions", page, searchTerm],
    queryFn: () => fetchIncomeOptions(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useIncomeOptions:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
};

/**
 * Get job Title
 */

const fetchJobTitles = async (
  jobId: number,
  page: number = 1,
  limit: number = 100,
  searchTerm = ""
): Promise<JobTitleResponse> => {
  try {
    const response = await api.get<JobTitleResponse>(
      "/v1/settings/profession_job_title",
      {
        params: { job_id: jobId, page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching job titles:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useJobTitles = (
  jobId: number,
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<JobTitleResponse, Error>({
    queryKey: ["job-titles", jobId, searchTerm],
    queryFn: () => fetchJobTitles(jobId, page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useJobTitles:", error);
      Sentry.captureException(error);
    },
    enabled: !!jobId,
    keepPreviousData: true,
  });
};

/**
 * Get jobs
 */

const fetchJobs = async (
  page: number = 1,
  limit: number = 100,
  searchTerm: string = ""
): Promise<JobResponse> => {
  try {
    const response = await api.get<JobResponse>("/v1/settings/profession_job", {
      params: { page, limit, q: searchTerm },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching jobs:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useJobs = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<JobResponse, Error>({
    queryKey: ["jobs", page, searchTerm],
    queryFn: () => fetchJobs(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useJobs:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    enabled: true,
  });
};

/**
 * Get specialization
 */

export const fetchSpecializationOptions = async (
  qualification_id: number,
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<SpecializationResponse> => {
  try {
    const response = await api.get<SpecializationResponse>(
      "/v1/settings/specialization",
      {
        params: {
          page,
          limit,
          qualification_id: qualification_id,
          q: searchTerm,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching specializations:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export function useSpecializationsQuery(
  qualification_id: number,
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm: string
) {
  return useQuery<SpecializationResponse, Error>({
    queryKey: ["specializationOptions", qualification_id, page, searchTerm],
    queryFn: () =>
      fetchSpecializationOptions(qualification_id, page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useSpecializationsQuery:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
}

/**
 * Get Qualification
 */

const fetchQualifications = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/qualification",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching qualifications:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export function useQualificationsQuery(
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm: string
) {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["qualifications", page, searchTerm],
    queryFn: () => fetchQualifications(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useQualificationsQuery:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
  });
}

/**
 * fetch marital status
 */

const fetchMaritalStatusOptions = async (
  page: number,
  limit: number,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/marital-status",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching marital statuses:", error);
    Sentry.captureException(error);
    throw new Error("Failed to fetch marital statuses");
  }
};

export const useMaritalStatusOptions = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["maritalStatusOptions", page, searchTerm],
    queryFn: () => fetchMaritalStatusOptions(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useMaritalStatusOptions:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
  });
};

/**
 * fetch mother tongue
 */
const fetchMotherTongue = async (
  page = 1,
  limit = QUERY_LIMIT,
  searchTerm = ""
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>(
      "/v1/settings/mother-tongue",
      {
        params: { page, limit, q: searchTerm },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching mother tongue:", error);
    Sentry.captureException(error);
    throw new Error("Failed to fetch mother tongue");
  }
};

export const useMotherTongueOptions = (
  page: number = 1,
  limit: number = 100,
  searchTerm: string
) => {
  return useQuery<GetSettingsResponse, Error>({
    queryKey: ["motherTongueOptions", page, searchTerm],
    queryFn: () => fetchMotherTongue(page, limit, searchTerm),
    onError: (error) => {
      console.error("Error in useMotherTongueOptions:", error);
      Sentry.captureException(error);
    },
    keepPreviousData: true,
  });
};

/**
 * fetch caste
 */

export const fetchCasteOptions = async (
  page = 1,
  limit = QUERY_LIMIT
): Promise<GetSettingsResponse> => {
  try {
    const response = await api.get<GetSettingsResponse>("/v1/settings/caste", {
      params: { page, limit },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching castes:", error);
    Sentry.captureException(error);
    throw error.response || error;
  }
};

export function useCasteOptions() {
  return useQuery("casteOptions", () => fetchCasteOptions(1, 100));
}

/**
 * getProfile created for
 */
export const getProfileCreatedFor = async (): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    api
      .get("/v1/settings/profile-created-for")
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
        Sentry.captureException(error);
      });
  });
};

/**
 * get Country
 */
// DONE

export const getCountryCode = async (): Promise<
  AxiosResponse<GetCountryResponse>
> => {
  try {
    return await api.get<GetCountryResponse>("/v1/settings/countries");
  } catch (error: any) {
    Sentry.captureException(error);
    throw error.response;
  }
};

export const useCountryCodeOptions = () => {
  return useQuery<AxiosResponse<GetCountryResponse>, Error>({
    queryKey: ["countryCodes"],
    queryFn: getCountryCode,
    onError: (error) => {
      console.error("Error in useCountryCodeOptions:", error);
      Sentry.captureException(error);
    },
    staleTime: 1000 * 60 * 5,
  });
};

/**
 *
 * @param param0 Get Accepted Interest Request
 * @returns
 */
const getAcceptedInterestRequests = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<ProfileResponse>> => {
  try {
    const response = await api.get<ProfileResponse>(
      `/v1/user/profile/interests/accepted`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching accepted interest requests:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetAcceptedInterestRequests = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["acceptedInterestRequests", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getAcceptedInterestRequests({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<ProfileResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.map((page) => page?.data.data ?? []),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetAcceptedInterestRequests:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Get Declined Interest Requests
 * @param param0
 * @returns
 */
const getDeclinedInterestRequests = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<ProfileResponse>> => {
  try {
    const response = await api.get<ProfileResponse>(
      `/v1/user/profile/interests/declined`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );

    return response;
  } catch (error) {
    console.error("Error fetching declined interest requests:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetDeclinedInterestRequests = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["declinedInterestRequests", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getDeclinedInterestRequests({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<ProfileResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.map((page) => page?.data.data ?? []),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetDeclinedInterestRequests:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Get sent interest requests
 * @param param0
 * @returns
 */
const getSentInterestRequests = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<InterestRequestResponse>> => {
  try {
    const response = await api.get<InterestRequestResponse>(
      `/v1/user/profile/interests/sent`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );

    return response;
  } catch (error) {
    console.error("Error fetching sent interest requests:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetSentInterestRequests = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["sentInterestRequests", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getSentInterestRequests({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<InterestRequestResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.flatMap(
        (page) => page.data.data?.map((request) => request.receiver) || []
      ),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetSentInterestRequests:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * get recieved interest requests
 * @param param0
 * @returns
 */
const getReceivedInterestRequests = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<InterestRequestResponse>> => {
  try {
    const response = await api.get<InterestRequestResponse>(
      `/v1/user/profile/interests/received`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );

    return response;
  } catch (error) {
    console.error("Error fetching received interest requests:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetReceivedInterestRequests = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["receivedInterestRequests", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getReceivedInterestRequests({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<InterestRequestResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.flatMap(
        (page) => page.data.data?.map((request) => request.sender) ?? []
      ),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetReceivedInterestRequests:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Get Notifications
 */

const getNotifications = async ({
  pageParam = 1,
  limit,
}: {
  pageParam?: number;
  limit?: number;
}): Promise<AxiosResponse<GetNotificationsResponse>> => {
  try {
    const response = await api.get<GetNotificationsResponse>(
      `/v1/user/profile/notification`,
      {
        params: { page: pageParam, limit },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetNotifications = (limit: number = QUERY_LIMIT) => {
  return useInfiniteQuery({
    queryKey: ["notifications", { limit }],
    queryFn: ({ pageParam = 1 }) => getNotifications({ pageParam, limit }),
    getNextPageParam: (lastPage: AxiosResponse<GetNotificationsResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.flatMap((page) => page.data.data ?? []),
      pageParams: data.pageParams,
    }),
    onError: (error: ErrorResponse) => {
      console.error("Error in useGetNotifications:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Delete Notification
 */

export const deleteNotification = async (
  notificationId: string
): Promise<AxiosResponse<DeleteNotificationResponse>> => {
  try {
    return await api.delete<DeleteNotificationResponse>(
      `/v1/user/profile/notification/${notificationId}`
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response) {
      throw error.response.data as DeleteNotificationAPIError;
    }
    Sentry.captureException(error);
    throw {
      statusCode: 500,
      message: "An unknown error occurred",
      error: "UnknownError",
    } as DeleteNotificationAPIError;
  }
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<DeleteNotificationResponse>,
    DeleteNotificationAPIError,
    string
  >({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (error) => {
      console.error("Error in useDeleteNotification:", error.message);
      Sentry.captureException(error);
    },
  });
};

export const markNotificationAsRead = async (
  notificationId: string
): Promise<AxiosResponse<ReadUpdateNotificationResponse>> => {
  try {
    return await api.patch<ReadUpdateNotificationResponse>(
      `/v1/user/profile/notification/${notificationId}`
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response) {
      throw error.response.data as ReadUpdateNotificationAPIError;
    }
    Sentry.captureException(error);
    throw {
      statusCode: 500,
      message: "An unknown error occurred",
      error: "UnknownError",
    } as ReadUpdateNotificationAPIError;
  }
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<ReadUpdateNotificationResponse>,
    ReadUpdateNotificationAPIError,
    string
  >({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (error) => {
      console.error("Error in mark notification as read:", error.message);
      Sentry.captureException(error);
    },
  });
};

/**
 * get ignored users details infinite
 * @param param0
 * @returns
 */

const getIgnoredUserDetails = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<GetIgnoredResponse> => {
  try {
    const response = await api.get<GetIgnoredResponse>(
      `/v1/user/profile/ignored`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Error fetching ignored user details:", error);
    Sentry.captureException(error);
    throw error.response?.data ?? { message: "An unknown error occurred" };
  }
};

export const useGetIgnoredUsersInfinite = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery<GetIgnoredResponse, APIErrorResponse>({
    queryKey: ["ignoredUsers", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getIgnoredUserDetails({ pageParam, limit, filters }),
    getNextPageParam: (lastPage) => {
      const totalPages = lastPage.pagination.total_pages;
      const currentPage = lastPage.pagination.page;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data: InfiniteData<GetIgnoredResponse>) => ({
      pages: data.pages.map((page) => ({
        ...page,
        data: page.data.map((profile) => profile), // Ensuring the response type remains `IgnoredResponse`
      })),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetIgnoredUsersInfinite:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * get favourite users details infinte
 * @param param0
 * @returns
 */
const getFavouriteUserDetails = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<FavouriteResponse>> => {
  try {
    const response = await api.get<FavouriteResponse>(
      `/v1/user/profile/favourites`,
      {
        params: { page: pageParam, limit, ...filters },
      }
    );

    return response;
  } catch (error) {
    console.error("Error fetching favourite user details:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetFavouriteUsersInfinite = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["favouriteUsers", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getFavouriteUserDetails({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<FavouriteResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.flatMap(
        (page) => page.data.data?.map((favorite) => favorite.to_profile) ?? []
      ),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetFavouriteUsersInfinite:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * get users details infinte
 * @param param0
 * @returns
 */
const getUsersDetailInfinite = async ({
  pageParam = 1,
  limit,
  filters,
}: {
  pageParam?: number;
  limit?: number;
  filters?: Record<string, any>;
}): Promise<AxiosResponse<ProfileResponse>> => {
  try {
    const response = await api.get<ProfileResponse>(`/v1/user/profile/`, {
      params: { page: pageParam, limit, ...filters },
    });

    return response;
  } catch (error) {
    console.error("Error fetching user details:", error);
    Sentry.captureException(error);
    throw error;
  }
};

export const useGetUsersInfinite = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["usersList", { limit, filters }],
    queryFn: ({ pageParam = 1 }) =>
      getUsersDetailInfinite({ pageParam, limit, filters }),
    getNextPageParam: (lastPage: AxiosResponse<ProfileResponse>) => {
      const totalPages = lastPage.data.pagination?.total_pages ?? 0;
      const currentPage = lastPage.data.pagination?.page ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data) => ({
      pages: data.pages.flatMap((page) => page.data?.data ?? []),
      pageParams: data.pageParams,
    }),
    onError: (error) => {
      console.error("Error in useGetUsersInfinite:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Get Search Profile
 */

const fetchProfiles = async (
  page = 1,
  limit = QUERY_LIMIT,
  filters: Record<string, any> = {}
): Promise<GetProfilesResponse> => {
  try {
    const response: AxiosResponse<GetProfilesResponse> = await api.post(
      "/v1/user/profile/search-profile",
      filters,
      {
        params: { page, limit },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching profiles:", error);
    Sentry.captureException(error);
    throw {
      status: error.response?.status || 500,
      message: error.message,
    } as ApiError;
  }
};

export const useSearchProfilesInfinite = (
  limit: number = QUERY_LIMIT,
  filters: Record<string, any> = {}
) => {
  return useInfiniteQuery<GetProfilesResponse, ApiError, GetProfilesResponse>({
    queryKey: ["profilesList", { limit, filters }],
    queryFn: ({ pageParam = 1 }) => fetchProfiles(pageParam, limit, filters),
    getNextPageParam: (lastPage) => {
      const totalPages = lastPage.pagination.total_pages;
      const currentPage = lastPage.pagination.page;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: QUERY_LIMIT * 60 * 1000,
    retry: 2,
    select: (data: InfiniteData<GetProfilesResponse>) => ({
      ...data,
      pages: data.pages.map((page) => ({ ...page, data: page.data })),
    }),
    onError: (error) => {
      console.error("Error in useSearchProfilesInfinite:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Fetches UserId details
 * @param userId
 * @returns
 */
export interface UserProfileApiResponse {
  message: string;
  data: UserDetailResponse;
  is_premium: boolean;
  maxProfileViewLimit: number;
  profileViewCount: number;
}

export interface UserDetailResponse {
  id: string;
  is_profile_completed: boolean;
  profile_created_for: string;
  gender: string;
  mother_tongue: string;
  first_name: string;
  last_name: string;
  email: string;
  dob: string;
  caste: string | null;
  mobile_number: string;
  country_code: string;
  marital_status: string;
  number_of_children: number | null;
  is_premium: boolean;
  is_active: boolean;

  // Profession Details
  profession_job: string | null;
  profession_jobTitle: string | null;
  profession_annual_income: string | null;

  profession_country: string | null;
  profession_country_id: string | null;

  profession_region: string | null;
  profession_region_id: string | null;

  profession_district: string | null;
  profession_district_id: string | null;

  profession_panchayath: string | null;
  profession_panchayath_id: string | null;

  profession_qualification: string | null;
  profession_specialization: string | null;
  profession_institution_name: string | null;

  // Family Details
  family_father_name: string | null;
  family_father_mobile_number: string | null;
  family_father_country: string | null;
  family_father_occupation: string | null;
  family_father_occupation_note: string | null;
  family_mother_name: string | null;
  family_mother_mobile_number: string | null;
  family_mother_country: string | null;
  family_mother_occupation: string | null;
  family_mother_occupation_note: string | null;
  family_status: string | null;
  family_net_worth: string | null;
  family_sibling_count: number | null;
  family_sibling:
    | {
        name: string;
        settled_status: string;
        note: string;
      }[]
    | null;

  // Wellness Details
  wellness_height: number | null;
  wellness_weight: number | null;
  wellness_eating_habits: string | null;
  wellness_physically_challenged: boolean | null;
  wellness_medicine_intaking: boolean | null;
  wellness_note: string | null;

  // Horoscope Details
  horoscope_preference: string | null;
  horoscope_star: string | null;
  horoscope_dosham: string | null;
  horoscope_raasi: string | null;
  horoscope_birth_time: string | null;
  horoscope_birth_place: string | null;
  horoscope_file: {
    url: string | null;
    key: string | null;
  } | null;

  // Address Details
  address_house_name: string | null;

  address_country: string | null;
  address_country_id: string | null;

  address_region: string | null;
  address_region_id: string | null;

  address_district: string | null;
  address_district_id: string | null;

  address_panchayath: string | null;
  address_panchayath_id: string | null;

  address_pin_number: string | null;

  // Preferences
  about_yourself: string | null;
  partner_preference: string | null;

  // Relationship Data
  favourite_to_profile: {
    id: string;
    from_profile_id: string;
  }[];

  receivedInterests: {
    id: string;
    status: string;
    createdAt: Date;
    acceptedAt: Date | null;
    rejectedAt: Date | null;
  }[];

  sentInterests: {
    id: string;
    status: string;
    createdAt: Date;
    acceptedAt: Date | null;
    rejectedAt: Date | null;
  }[];

  ignored_to_profile: {
    id: string;
    createdAt: string;
  }[];

  ignored_from_profile: {
    id: string;
    createdAt: string;
  }[];

  partner_match: {
    id: string;
    requester_id: string;
    partner_id: string;
    is_completed: boolean;
    created_at: Date;
  }[];

  // Profile Images
  profile_images: {
    key: string;
    url: string;
  }[];

  maxProfileViewLimit: number;
  profileViewCount: number;
  isLegitPremium: boolean;
}

const getUserDetail = async (
  userId: string
): Promise<AxiosResponse<UserProfileApiResponse>> => {
  try {
    const response = await api.get<UserProfileApiResponse>(
      `/v1/user/profile/${userId}`
    );
    return response;
  } catch (error: any) {
    console.error(`Error fetching user detail for ${userId}:`, error);
    throw error.response ?? error;
  }
};

export const useGetUserDetail = (userId: string) => {
  return useQuery<UserProfileApiResponse, ApiStatusError>({
    queryKey: ["userDetail", userId],
    queryFn: async () => {
      const response = await getUserDetail(userId);
      return response.data;
    },
    enabled: Boolean(userId),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
    onError: (error: ApiStatusError) => {
      console.error("Error in useGetUserDetail:", error);
      if (error?.data?.message) {
        message.error(error.data.message);
      }
    },
  });
};

/**
 * Get Plans Subscription Details
 * @returns
 */

export const fetchSubscriptionData =
  async (): Promise<SubscriptionResponse> => {
    try {
      const response = await api.get<SubscriptionResponse>("/v1/plans/");
      return response.data;
    } catch (error: any) {
      const axiosError = error as AxiosError<SubscriptionAPIError>;
      console.error(
        "Error fetching subscription data:",
        axiosError.response?.data?.message || axiosError.message
      );
      Sentry.captureException(error);
      throw (
        axiosError.response?.data || {
          message: "Failed to fetch subscription data",
          error: "Unknown error",
        }
      );
    }
  };

export const useSubscriptionData = () => {
  return useQuery<SubscriptionResponse, SubscriptionAPIError>({
    queryKey: ["subscriptions"],
    queryFn: fetchSubscriptionData,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    onError: (error) => {
      console.error("Error in useSubscriptionData:", error.message);
        Sentry.captureException(error);
    },
  });
};

/**
 *
 * @param interestId Declines a user Interest Sented
 * @returns
 */

type APIError = AxiosError<{ message?: string }>;

export const declineRequest = async (
  interestId: string
): Promise<AxiosResponse<DeclineInterestResponse>> => {
  try {
    return await api.put<DeclineInterestResponse>(
      `/v1/user/profile/${interestId}/decline-interest`
    );
  } catch (error: any) {
    console.error(`Error declining interest request (${interestId}):`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useDeclineRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<DeclineInterestResponse>, APIError, string>({
    mutationFn: declineRequest,
    onSuccess: (response) => {
      const { sentedInterest, recievedInterest } = response.data.data;

      if (sentedInterest)
        queryClient.invalidateQueries({ queryKey: ["sentInterestRequests"] });
      if (recievedInterest)
        queryClient.invalidateQueries({
          queryKey: ["receivedInterestRequests"],
        });

      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
    onError: (error) => {
      console.error(
        "Error in useDeclineRequest:",
        error.response?.data?.message || error.message
      );
      Sentry.captureException(error);
    },
  });
};

/**
 *  Send Otp
 * @param data
 * @returns
 */
export const sendOtp = async (data: {
  country_code?: string;
  mobile_number?: string;
  email?: string;
}): Promise<AxiosResponse<SendOtpResponse>> => {
  try {
    const response = await api.post<SendOtpResponse>("/v1/user/send-otp", data);
    return response;
  } catch (error: any) {
    console.error("Error sending OTP:", error);
    Sentry.captureException(error);
    throw new Error(error?.response?.data?.message || "Failed to send OTP");
  }
};

export const useSendOtp = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<SendOtpResponse>,
    Error,
    {
      country_code?: string;
      mobile_number?: string;
      email?: string;
    }
  >({
    mutationFn: sendOtp,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];

      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey })
      );
    },
    onError: (error) => {
      console.error("Error in useSendOtp:", error.message);
        Sentry.captureException(error);
    },
  });
};

/**
 * Verify Otp
 * @param data
 * @returns
 */

export const verifyOtp = async (data: {
  country_code?: string;
  mobile_number?: string;
  email?: string;
  otp: string;
}): Promise<AxiosResponse<VerifyOtpResponse>> => {
  try {
    return await api.post<VerifyOtpResponse>("/v1/user/verify-otp", data);
  } catch (error: any) {
    console.error("Error verifying OTP:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useVerifyOtp = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    Error,
    {
      country_code?: string;
      mobile_number?: string;
      email?: string;
      otp: string;
    }
  >({
    mutationFn: verifyOtp,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey })
      );
    },
    onError: (error) => {
      console.error("Error in useVerifyOtp:", error.message);
        Sentry.captureException(error);
    },
  });
};

/**
 * Reset Password
 * @param data
 * @returns
 */
export const resetPassword = async (data: {
  mobile_number?: string;
  country_code?: string;
  email?: string;
  password: string;
}): Promise<AxiosResponse> => {
  try {
    const response = await api.post("/v1/user/change-password", {
      mobile_number: data.mobile_number,
      country_code: data.country_code,
      email: data.email,
      new_password: data.password,
    });
    return response;
  } catch (error: any) {
    console.error("Error resetting password:", error);
    Sentry.captureException(error);
    throw new Error(
      error?.response?.data?.message || "Failed to reset password"
    );
  }
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    Error,
    {
      mobile_number?: string;
      country_code?: string;
      email?: string;
      password: string;
    }
  >({
    mutationFn: resetPassword,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];

      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey })
      );
    },
    onError: (error) => {
      console.error("Error in useResetPassword:", error.message);
      Sentry.captureException(error);
    },
  });
};

/**
 * Login with password
 * @param data
 * @returns
 */

export const loginWithPassword = async (data: {
  userId?: string;
  password?: string;
  mobile_number?: string;
  country_code?: string;
}): Promise<AxiosResponse<LoginResponse>> => {
  try {
    const response = await api.post<LoginResponse>("/v1/user/login", data);
    return response;
  } catch (error: any) {
    console.error("Error logging in:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useLoginWithPassword = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<LoginResponse>,
    unknown,
    {
      userId?: string;
      password?: string;
      mobile_number?: string;
      country_code?: string;
    }
  >({
    mutationFn: loginWithPassword,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];

      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useLoginWithPassword:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * logout
 * @returns
 */
const postLogout = async (): Promise<AxiosResponse> => {
  try {
    const response = await api.post(`/v1/user/logout/`);
    return response;
  } catch (error: any) {
    console.error("Logout failed:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useLogout = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<AxiosResponse, unknown>({
    mutationFn: postLogout,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );

      dispatch(logout());
      navigate("/login", { replace: true });
    },
    onError: (error) => {
      console.error("Error in useLogout:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Get OnBoardingData
 */

const fetchOnBoarding = async (): Promise<
  AxiosResponse<GetOnBoardingResponse>
> => {
  try {
    return await api.get<GetOnBoardingResponse>("/v1/user/profile/onboarding");
  } catch (error: any) {
    console.error("Error fetching onboarding:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useGetOnBoarding = () => {
  return useQuery<AxiosResponse<GetOnBoardingResponse>, unknown>({
    queryKey: ["onBoarding"],
    queryFn: fetchOnBoarding,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: QUERY_LIMIT * 60 * 1000, // QUERY_LIMIT minutes
    onError: (error) => {
      console.error("Error in useGetOnBoarding:", error);
        Sentry.captureException(error);
    },
  });
};

/**
 * Submit OnBoardingData
 */

const submitOnBoarding = async (
  data: ProfileType
): Promise<AxiosResponse<SubmitOnBoardingResponse>> => {
  try {
    return await api.post<SubmitOnBoardingResponse>(
      "/v1/user/profile/onboarding",
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  } catch (error: any) {
    console.error("Error submitting onboarding:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useSubmitOnBoarding = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<SubmitOnBoardingResponse>,
    unknown,
    ProfileType
  >({
    mutationFn: submitOnBoarding,
    onSuccess: () => {
      const queriesToInvalidate = ["userProfile", "usersList"];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useSubmitOnBoarding:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * submit onboarding user data
 * @param data
 * @returns
 */

const createProfile = async (
  data: ProfileType
): Promise<AxiosResponse<CreateProfileResponse>> => {
  try {
    return await api.post<CreateProfileResponse>("/v1/user/profile/", data);
  } catch (error: any) {
    console.error("Error creating profile:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useCreateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<CreateProfileResponse>,
    unknown,
    ProfileType
  >({
    mutationFn: createProfile,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useCreateProfile:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * registers user data
 * @param newUser
 * @returns
 */

export const addRegisterData = async (
  newUser: Partial<UserDataType>
): Promise<AxiosResponse<AddRegisterResponse>> => {
  try {
    return await api.post<AddRegisterResponse>("/v1/user/register", newUser);
  } catch (error: any) {
    console.error("Error registering user:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useAddRegisterData = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<AddRegisterResponse>,
    unknown,
    Partial<UserDataType>
  >({
    mutationFn: addRegisterData,
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "subscriptions",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useAddRegisterData:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * add or remove from ignore
 * @param toProfileId
 * @returns
 */
export const addToFavourites = async (
  toProfileId: string
): Promise<AxiosResponse<void>> => {
  try {
    return await api.put(`/v1/user/profile/${toProfileId}/favourite`);
  } catch (error: any) {
    console.error(`Error adding profile ${toProfileId} to favourites:`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useToggleFavourites = () => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<void>, unknown, string>(addToFavourites, {
    onSuccess: () => {
      const queriesToInvalidate = ["favouriteUsers", "usersList"];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useToggleFavourites:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 *  toggle ignore
 */
export const toggleIgnore = async (
  toProfileId: string
): Promise<ToggleIgnoreResponse> => {
  try {
    const response = await api.put<ToggleIgnoreResponse>(
      `/v1/user/profile/${toProfileId}/ignore`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error ignoring profile ${toProfileId}:`, error);
    Sentry.captureException(error);
    throw error.response?.data ?? { message: "An unknown error occurred" };
  }
};

export const useToggleIgnore = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ToggleIgnoreResponse,
    APIToggleIgnoreErrorResponse,
    string
  >(toggleIgnore, {
    onSuccess: () => {
      const queriesToInvalidate = [
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "sentInterestRequests",
        "receivedInterestRequests",
        "ignoredUsers",
        "favouriteUsers",
        "usersList",
        "profilesList",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error("Error in useToggleIgnore:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Send Interest Request
 * @param toProfileId
 * @returns
 */

export const sendRequest = async (
  toProfileId: string
): Promise<AxiosResponse<SendRequestResponse>> => {
  try {
    return await api.put<SendRequestResponse>(
      `/v1/user/profile/${toProfileId}/send-interest`
    );
  } catch (error: any) {
    console.error(
      `Error sending interest request to profile ${toProfileId}:`,
      error
    );
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useSendRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<SendRequestResponse>, unknown, string>(
    sendRequest,
    {
      onSuccess: () => {
        const queriesToInvalidate = [
          "sentInterestRequests",
          "favouriteUsers",
          "usersList",
        ];
        queriesToInvalidate.forEach((queryKey) =>
          queryClient.invalidateQueries([queryKey])
        );
      },
      onError: (error) => {
        console.error("Error in useSendRequest:", error);
        Sentry.captureException(error);
      },
    }
  );
};

/**
 * Accepts a user Interest Sented
 * @param interestId
 * @returns
 */
enum RequestStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}
export interface InterestRequest {
  id: string;
  senderId: string;
  receiverId: string;
  status: RequestStatus;
  createdAt: Date;
  acceptedAt: Date;
  rejectedAt: Date;
}

export interface AcceptInterestResponse {
  message: string;
  data: {
    recievedInterest: InterestRequest;
    sentedInterest: InterestRequest;
    isMatch: boolean;
  };
}

export interface AcceptInterestAPIError {
  response?: {
    data?: {
      message: string;
      data?: any;
    };
    status?: number;
  };
  message: string;
}

export interface ApiErrorResponse {
  message: string;
  errors: Record<string, { message: string }>;
}

export const acceptRequest = async (
  interestId: string
): Promise<AxiosResponse<AcceptInterestResponse>> => {
  try {
    const response = await api.put<AcceptInterestResponse>(
      `/v1/user/profile/${interestId}/accept-interest`
    );
    return response;
  } catch (error: any) {
    console.error(
      `Error accepting interest request (ID: ${interestId}):`,
      error
    );
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useAcceptRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<AcceptInterestResponse>,
    AcceptInterestAPIError,
    string
  >(acceptRequest, {
    onSuccess: () => {
      const queriesToInvalidate = [
        "sentInterestRequests",
        "acceptedInterestRequests",
        "declinedInterestRequests",
        "receivedInterestRequests",
        "favouriteUsers",
        "usersList",
      ];
      queriesToInvalidate.forEach((queryKey) =>
        queryClient.invalidateQueries([queryKey])
      );
    },
    onError: (error) => {
      console.error(
        "Error in useAcceptRequest:",
        error.response?.data?.message || error.message
      );
      Sentry.captureException(error);
    },
  });
};

export const useCancelRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<{
      message: string;
    }>,
    AxiosError<ApiErrorResponse>,
    ObjectId
  >({
    mutationFn: async (toProfileId) => {
      const response = await api.put(
        `/v1/user/profile/${toProfileId}/cancel-interest`
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sentInterestRequests"] });
      queryClient.invalidateQueries({ queryKey: ["favouriteUsers"] });
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
  });
};

/**
 * Initiate PhonePay purchase plan Rz
 * @param interestId
 * @returns
 */

export const RzPurchasePlan = async (
  planId: string
): Promise<AxiosResponse<InitiateRzPurchasePlanResponse>> => {
  try {
    return await api.post<InitiateRzPurchasePlanResponse>(
      "/v1/plans/purchase_plan",
      { planId }
    );
  } catch (error: any) {
    console.error(`Error purchasing plan (ID: ${planId}):`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useRzPurchasePlan = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<InitiateRzPurchasePlanResponse>,
    ApiStatusError,
    string
  >(RzPurchasePlan, {
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      queryClient.invalidateQueries(["myDetail"]);
    },
    onError: (error: any) => {
      console.error("Error in useRzPurchasePlan:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Update profile
 */
export const updateProfile = async (
  profileData: Record<string, any>
): Promise<AxiosResponse<any>> => {
  try {
    const response = await api.put(`/v1/user/profile/`, profileData);
    return response;
  } catch (error: any) {
    console.error("Error updating profile:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useUpdateProfile = (userId: ObjectId) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<any>, unknown, Record<string, any>>(
    updateProfile,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userDetail", userId]);
        queryClient.invalidateQueries(["myDetail"]);
        queryClient.invalidateQueries(["usersList"]);
      },
      onError: (error) => {
        console.error("Error in useUpdateProfile:", error);
        Sentry.captureException(error);
      },
    }
  );
};

/**
 * Add photo
 */

export const addPhoto = async (
  formData: FormData
): Promise<AxiosResponse<PhotoUploadResponse>> => {
  try {
    const response = await api.post<PhotoUploadResponse>(
      "/v1/user/profile/profile-image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      }
    );
    return response;
  } catch (error: any) {
    console.error("Error uploading photo:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useAddPhoto = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<PhotoUploadResponse>, unknown, FormData>({
    mutationFn: addPhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetail", userId] });
      queryClient.invalidateQueries(["myDetail"]);
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
    onError: (error) => {
      console.error("Error in useAddPhoto:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Delete photo
 */
interface DeletePhotoResponse {
  message: string;
}

export const deletePhoto = async (
  key: string
): Promise<AxiosResponse<DeletePhotoResponse>> => {
  try {
    const response = await api.delete<DeletePhotoResponse>(
      "/v1/user/profile/profile-image",
      {
        data: { key },
      }
    );
    return response;
  } catch (error: any) {
    console.error("Error deleting photo:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useDeletePhoto = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<DeletePhotoResponse>, unknown, string>({
    mutationFn: deletePhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetail", userId] });
      queryClient.invalidateQueries(["myDetail"]);
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
    onError: (error) => {
      console.error("Error in useDeletePhoto:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Delete horoscope file
 */
export const deleteHoroscope = async (
  key: string
): Promise<AxiosResponse<{ message: string }>> => {
  try {
    const response = await api.delete<{ message: string }>(
      "/v1/user/profile/horoscope",
      { data: { key } }
    );
    return response;
  } catch (error: any) {
    console.error("Error deleting horoscope:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useDeleteHoroscope = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<{ message: string }>, unknown, string>({
    mutationFn: deleteHoroscope,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetail", userId] });
      queryClient.invalidateQueries(["myDetail"]);
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
    onError: (error) => {
      console.error("Error in useDeleteHoroscope:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Upoad horoscope file
 */

export const uploadHoroscopeFile = async (
  file: File
): Promise<AxiosResponse<UploadHoroscopeApiResponse>> => {
  try {
    const formData = new FormData();
    formData.append("horoscope_file", file);

    const response = await api.post<UploadHoroscopeApiResponse>(
      "/v1/user/profile/horoscope",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error: any) {
    console.error("Error uploading horoscope file:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useUploadHoroscope = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<UploadHoroscopeApiResponse>, unknown, File>({
    mutationFn: uploadHoroscopeFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetail", userId] });
      queryClient.invalidateQueries(["myDetail"]);
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    },
    onError: (error) => {
      console.error("Error in useUploadHoroscope:", error);
      Sentry.captureException(error);
    },
  });
};

/**
 * Download profile
 */

const downloadHoroscopeFile = async (
  userId: string
): Promise<DownloadResponse> => {
  try {
    const response = await api.get<Blob>(
      `/v1/user/profile/horoscope-file-download/${userId}`,
      {
        responseType: "arraybuffer",
      }
    );
    const contentDisposition = response.headers["content-disposition"] || "";

    // Fallback filename if header parsing fails
    let filename = `Profile_${userId}.pdf`;
    const filenameMatch =
      contentDisposition.match(/filename="(.+)"/) ||
      contentDisposition.match(/filename=([^;]+)/);

    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/"/g, "");
    }

    // Create Blob directly from arraybuffer
    const blob = new Blob([response.data], { type: "application/pdf" });

    return {
      data: blob,
      filename: filename,
    };
  } catch (error: any) {
    console.error("Download error:", error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const useDownloadHoroscopeFile = () => {
  return useMutation({
    mutationFn: downloadHoroscopeFile,
    onSuccess: (response: { data: Blob; filename: string }) => {
      try {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = response.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("PDF download creation error:", error);
        Sentry.captureException(error);
        message.error("Could not download the PDF. Please try again.");
      }
    },
    onError: (error) => {
      console.error("Horoscope download mutation error:", error);
      Sentry.captureException(error);
      message.error(
        "Failed to download horoscope. Please check your connection."
      );
    },
  });
};

/**
 * Initiate PhonePay purchase plan
 * @param interestId
 * @returns
 */

export const purchasePlan = async (
  planId: string
): Promise<AxiosResponse<InitiatePurchasePlanResponse>> => {
  try {
    return await api.post<InitiatePurchasePlanResponse>(
      "/v1/plans/purchase_plan",
      { planId }
    );
  } catch (error: any) {
    console.error(`Error purchasing plan (ID: ${planId}):`, error);
    Sentry.captureException(error);
    throw error.response ?? error;
  }
};

export const usePurchasePlan = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<InitiatePurchasePlanResponse>,
    ApiStatusError,
    string
  >(purchasePlan, {
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      queryClient.invalidateQueries(["myDetail"]);
    },
    onError: (error: any) => {
      console.error("Error in usePurchasePlan:", error);
      Sentry.captureException(error);
    },
  });
};

// Account Deletion Request
interface AccountDeletionRequestData {
  reason: string;
  additionalNotes?: string;
  confirmDeletion: boolean;
}

interface AccountDeletionResponse {
  message: string;
  data: {
    id: string;
    status: string;
    created_at: string;
  };
}

export const useSubmitAccountDeletionRequest = () => {
  return useMutation<
    AxiosResponse<AccountDeletionResponse>,
    AxiosError,
    AccountDeletionRequestData
  >({
    mutationFn: async (data) => {
      const response = await api.post("/v1/user/remove-account", data);
      return response.data;
    },
    onError: (error) => {
      console.error("Error in useSubmitAccountDeletionRequest:", error);
      Sentry.captureException(error);
    },
  });
};
